import { type FC, type KeyboardEvent, useCallback, useMemo } from "react";
import { useI18nContext } from "@hopper-b2b/i18n";
import type { ILodgingFilterState } from "@hopper-b2b/types";
import { Button, Chip } from "@material-ui/core";
import Cancel from "../../assets/client/cancel.svg?react";
import styles from "./styles.module.scss";

const AppliedFilterTag: FC<{
  filter: string;
  onDelete: (filter: string) => void;
}> = ({ filter, onDelete }) => {
  const { t } = useI18nContext();
  const handleDelete = useCallback(() => {
    onDelete(filter);
  }, [filter, onDelete]);

  const label = useMemo(() => {
    return t(`${filter}`);
  }, [filter, t]);

  const onKeyDownHandler = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === "Enter") {
        onDelete(filter);
      }
    },
    [filter, onDelete]
  );

  return (
    <Chip
      className={"applied-filter-chip"}
      label={label}
      aria-label={`Remove ${filter}`}
      onKeyDown={onKeyDownHandler}
      onDelete={handleDelete}
      color="secondary"
      deleteIcon={<Cancel />}
    />
  );
};

export interface AppliedFilterTagsProps {
  filters: ILodgingFilterState;
  resetFilters: () => { type: string };
  deleteFilter: (key: string) => void;
}

export const AppliedFilterTags = (props: AppliedFilterTagsProps) => {
  const { filters, resetFilters, deleteFilter } = props;

  const appliedFilters = useMemo(() => {
    const appliedFiltersArray = [];
    Object.keys(filters).forEach((key) => {
      if (
        ((key === "starRating" || key === "amenities") &&
          filters[key].length > 0) ||
        (key === "userRating" && filters[key]) ||
        (key === "priceRange" &&
          (filters[key].min !== filters[key].lowest ||
            filters[key].max !== filters[key].highest))
      ) {
        appliedFiltersArray.push(key);
      }
    });
    return appliedFiltersArray;
  }, [filters]);

  const handleDelete = useCallback(
    (key: string) => {
      deleteFilter(key);
    },
    [deleteFilter]
  );

  const onKeyDownHandler = useCallback(
    (e) => {
      if (e.key === "Enter") {
        resetFilters();
      }
    },
    [resetFilters]
  );

  const { t } = useI18nContext();

  if (appliedFilters?.length <= 0) return null;

  return (
    <div className={styles.AppliedFilterTags}>
      {appliedFilters.length > 0 && (
        <Button
          className="reset-filters"
          onKeyDown={onKeyDownHandler}
          onClick={resetFilters}
        >
          {t("resetFilters")}
        </Button>
      )}
      {appliedFilters.map((filter) => (
        <AppliedFilterTag
          key={filter}
          filter={filter}
          onDelete={handleDelete}
        />
      ))}
    </div>
  );
};
